import { createLogger, Log, type ILogger, type LogDeviceInformation } from '@core/logging';
import { enrichLogEvent } from '@core/swagger-client';
import { createCsrfMiddleware, createMiddleware, createStart } from '@tanstack/react-start';

/**
 * SSR request-tracing middleware.
 *
 * Logs every incoming server request (page render + server function) to Seq via the existing
 * @core/logging Log class: a request id (honouring an upstream x-request-id / x-correlation-id),
 * method, path, response status and duration. Runs server-side only.
 *
 * IMPORTANT: registering requestMiddleware on the start instance REPLACES TanStack Start's default
 * CSRF middleware, so createCsrfMiddleware() is re-included below to keep server functions protected.
 */

let serverLogger: ILogger | undefined;

// The SSR server ships Seq events through the backend's logging endpoint, exactly like the browser
// does — VITE_WEBINAR_API in production, the dev proxy target locally. If neither is set the POST
// simply fails and is swallowed (the Log class still console-logs in non-production).
function serverLogConfig() {
    const backendBaseUrl = (process.env.VITE_WEBINAR_API || process.env.VITE_WEBINAR_SERVER_API || '').replace(/\/+$/, '');
    // Device info is browser-shaped; on the server we just record that this came from SSR.
    const deviceInformation = {
        deviceType: 'server',
        browserInformation: { name: 'TanStack Start SSR', version: '' },
        operatingSystemInformation: { name: 'node', version: process.version }
    } as unknown as LogDeviceInformation;
    return { seqUrl: `${backendBaseUrl}/api/logging/`, isProduction: process.env.VITE_IS_PRODUCTION === 'true', deviceInformation };
}

const getServerLogger = (): ILogger => {
    if (serverLogger) return serverLogger;
    const { seqUrl, isProduction, deviceInformation } = serverLogConfig();
    serverLogger = new Log(seqUrl, isProduction, deviceInformation, (event) => {
        event['Source'] = 'frontend-ssr';
    });
    return serverLogger;
};

// The app logger (@core/logging's logError and friends) is configured once at boot on the server,
// so a failure in a loader or in a render that never reaches CreateLogger still reaches Seq. Every
// line is enriched with the account of its own request through enrichLogEvent.
if (import.meta.env.SSR) {
    const { seqUrl, isProduction, deviceInformation } = serverLogConfig();
    createLogger(seqUrl, isProduction, deviceInformation, enrichLogEvent);
}

const requestTracingMiddleware = createMiddleware({ type: 'request' }).server(async ({ next, request }) => {
    const requestId = request.headers.get('x-request-id') ?? request.headers.get('x-correlation-id') ?? globalThis.crypto.randomUUID();

    let path: string;
    try {
        path = new URL(request.url).pathname;
    } catch {
        path = request.url;
    }

    const startedAt = performance.now();

    try {
        const result = await next();
        const status = (result as { response?: Response }).response?.status;
        getServerLogger().information('SSR {Method} {Path} responded {StatusCode} in {ElapsedMs}ms', {
            RequestId: requestId,
            Method: request.method,
            Path: path,
            StatusCode: status,
            ElapsedMs: Math.round(performance.now() - startedAt)
        });
        return result;
    } catch (error) {
        getServerLogger().error('SSR {Method} {Path} failed after {ElapsedMs}ms', {
            RequestId: requestId,
            Method: request.method,
            Path: path,
            ElapsedMs: Math.round(performance.now() - startedAt),
            Error: error instanceof Error ? { name: error.name, message: error.message, stack: error.stack } : String(error)
        });
        throw error;
    }
});

export const startInstance = createStart(() => ({
    // Re-create TanStack Start's default CSRF middleware (only guards server functions, not page
    // loads — otherwise top-level navigations with Sec-Fetch-Site: none would be rejected).
    requestMiddleware: [requestTracingMiddleware, createCsrfMiddleware({ filter: (ctx) => (ctx as { handlerType?: string }).handlerType === 'serverFn' })]
}));
